/**
 * section-product-customizer-2.js
 *
 * Behavior for the product.customizer-2 template's two pop-ups:
 *  1. Color pop-up ("See All Colors") -- clicking a swatch swaps the large
 *     preview image only. Browsing only, no variant selection / add-to-cart.
 *  2. Zakeke customizer pop-up ("Start Customizing") -- lazy-loads Zakeke's
 *     real Shopify integration script the first time the modal opens.
 *
 * Both modals are the theme's existing <modal-dialog> web component
 * (assets/global.js, unmodified) -- this file only adds behavior on top of
 * the open/close it already provides via [data-modal-opener] and the
 * "open" class on .modal.
 *
 * Ported from WristCo (co-customizer2-*) and WRW (section-product-
 * customizer-2-*), per customizer-2-port-prompt.md. Zakeke mechanism below
 * is confirmed against WristCo's and WRW's own live stores; per the port
 * prompt, do one live confirmation fetch against an XP product before
 * trusting it here too.
 *
 * Zakeke integration mechanism:
 *  - There is no OAuth/token exchange and no separate JSON config endpoint.
 *    Zakeke's Shopify app renders the container, add-to-cart form, and its
 *    own <script> tags (culminating in a `zakekeDesigner({...})` call, with
 *    the per-product config already resolved server-side) directly into
 *    the HTML returned by /apps/zakeke?pid=<product id>. This is the same
 *    URL/pattern CLAUDE.md documents theme.liquid using for the full-page
 *    Zakeke flow.
 *  - That fragment lives inside a `.icartShopifyCartContent` wrapper. We
 *    fetch the page, scope everything to that wrapper, and:
 *      a. clone the #zakeke-addtocart form (a plain sibling element, not a
 *         script -- easy to miss, but Zakeke's iframe submits through it)
 *      b. clone any Zakeke <link> stylesheet (link tags apply automatically
 *         once connected to the document, no replay needed)
 *      c. recreate every <script> tag as a real element (scripts parsed via
 *         DOMParser are inert and never auto-execute -- they must be
 *         recreated to run)
 *  - Scoped to `.icartShopifyCartContent` specifically so we don't replay
 *    the rest of that fetched page's scripts (analytics, cart drawer init,
 *    etc.) a second time.
 *  - Loaded once per page view, on first modal open -- not torn down and
 *    reloaded on every open/close, since Zakeke's own script has no
 *    documented teardown API and re-calling zakekeDesigner() on the same
 *    container is unverified. If Zakeke needs a hard reset between opens,
 *    that needs Zakeke's own guidance.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Color pop-up: preload every swatch's large image once, on first open
   * ------------------------------------------------------------------- */
  /* Clicking a swatch swaps the main image's src directly, which for a
     not-yet-requested image means the browser has to fetch it before
     anything paints -- confirmed live on WRW/WristCo, a visible lag on
     the first click of each color. Preloading every swatch's large-image
     URL as soon as the modal opens means that fetch has already happened
     (or is in flight) by the time a color is actually clicked, so the
     swap reads from cache instead.

     Capped at 4 concurrent requests rather than firing all of them at
     once: with a couple hundred colors on some products, flooding the
     connection queue with that many simultaneous requests makes the swap
     slower, not faster, since whichever image is actually clicked ends up
     stuck in line behind the rest. A small fixed concurrency keeps steady
     background progress without starving the next click. */
  var colorPreloadState = null; /* null | 'done' */
  function preloadColorPreviewImages(modal) {
    if (colorPreloadState === 'done') return;
    colorPreloadState = 'done';

    var sources = [];
    modal.querySelectorAll('[data-color-swatch-btn]').forEach(function (btn) {
      var src = btn.getAttribute('data-large-image');
      if (src) sources.push(src);
    });

    var CONCURRENCY = 4;
    var nextIndex = 0;

    function loadNext() {
      if (nextIndex >= sources.length) return;
      var src = sources[nextIndex++];
      var img = new Image();
      img.onload = img.onerror = loadNext;
      img.src = src;
    }

    for (var i = 0; i < CONCURRENCY; i++) {
      loadNext();
    }
  }

  /* ---------------------------------------------------------------------
   * Color pop-up: swap large image on swatch click
   * ------------------------------------------------------------------- */
  function initColorPopup() {
    var modal = document.getElementById('ColorPopup2Modal');
    if (!modal) return;

    var mainImage = modal.querySelector('[data-color-popup-main-image]');
    var mainLabel = modal.querySelector('[data-color-popup-main-label]');
    var swatchButtons = modal.querySelectorAll('[data-color-swatch-btn]');
    var categoryButtons = modal.querySelectorAll('[data-color-category-btn]');

    swatchButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var largeSrc = btn.getAttribute('data-large-image');
        if (!mainImage || !largeSrc) return;

        mainImage.setAttribute('src', largeSrc);
        mainImage.removeAttribute('srcset');
        mainImage.setAttribute('alt', btn.getAttribute('data-alt') || '');

        if (mainLabel) {
          mainLabel.textContent = btn.getAttribute('data-variant-title') || '';
        }

        swatchButtons.forEach(function (other) {
          other.classList.remove('is-active');
        });
        btn.classList.add('is-active');
      });
    });

    /* -------------------------------------------------------------------
     * Category filter buttons: show/hide swatches already in the DOM.
     * One shared function drives both the click handlers and the initial
     * default-category call below, so the "currently-previewed color got
     * filtered out" fallback only has to live in one place -- it resolves
     * by calling .click() on the first visible swatch, reusing the
     * preview-swap handler registered just above instead of duplicating it.
     * ----------------------------------------------------------------- */
    function applyCategoryFilter(categoryId) {
      categoryButtons.forEach(function (catBtn) {
        catBtn.classList.toggle('is-active', catBtn.getAttribute('data-category') === categoryId);
      });

      var firstVisible = null;
      var activeStillVisible = false;

      swatchButtons.forEach(function (btn) {
        var categories = (btn.getAttribute('data-c2-categories') || '').split(' ');
        var isVisible = categoryId === 'all' || categories.indexOf(categoryId) !== -1;
        btn.classList.toggle('xp-c2-color-swatch-hidden', !isVisible);

        if (isVisible) {
          if (!firstVisible) firstVisible = btn;
          if (btn.classList.contains('is-active')) activeStillVisible = true;
        }
      });

      if (!activeStillVisible && firstVisible) {
        firstVisible.click();
      }
    }

    categoryButtons.forEach(function (catBtn) {
      catBtn.addEventListener('click', function () {
        applyCategoryFilter(catBtn.getAttribute('data-category'));
      });
    });

    if (categoryButtons.length) {
      var hasMostPopular = modal.querySelector('[data-color-category-btn][data-category="most-popular"]');
      applyCategoryFilter(hasMostPopular ? 'most-popular' : 'all');
    }
  }

  /* ---------------------------------------------------------------------
   * Zakeke customizer pop-up: load the real Zakeke fragment on first open
   * ------------------------------------------------------------------- */
  var ZAKEKE_APP_PROXY_PATH = '/apps/zakeke';
  var zakekeLoadState = null; /* null | 'loading' | 'loaded' | 'error' */

  /* ---------------------------------------------------------------------
   * "Edit Design" URL params, read once on init
   * ------------------------------------------------------------------- */
  /* assets/component-cart-form.js's Edit Design link (cart line item ->
     /products/<handle>?view=customizer-2&design=...|composition=...
     &variant=...&quantity=...) lands here wanting the modal to open
     already pointed at the customer's existing design instead of a blank
     one. Read once into a module-scoped object (matching this file's own
     zakekeLoadState/colorPreloadState idiom) rather than stamping DOM
     data-attributes -- there's already exactly one Zakeke mount on this
     template, so there's nothing per-instance to key the params by. */
  var zakekeEditParams = null;
  function readZakekeEditParamsFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var design = params.get('design');
    var composition = params.get('composition');
    return {
      design: design,
      composition: composition,
      variant: params.get('variant'),
      quantity: params.get('quantity'),
      hasEditParam: !!(design || composition)
    };
  }

  /* The customizer-2 template is reached via a plain product URL
     (/products/<handle>?view=customizer-2...), so the handle is always
     the first path segment -- no need for a Liquid-rendered data
     attribute just to recover it here. */
  function getProductHandleFromPath() {
    var match = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return match ? match[1] : null;
  }

  /* Mirrors the cart's "Edit Design" configuration-based link (assets/
     component-cart-form.js), which uses Zakeke's handle+variant route
     (/apps/zakeke/c/<handle>) rather than the pid route -- that's the
     route the "configuration-based edit" cart link has always used, so
     relaying it through unchanged is what makes editing that branch's
     designs work the same way the design/pid branch already does. */
  function buildZakekeFetchUrl(mount) {
    var edit = zakekeEditParams || {};

    if (edit.composition) {
      var handle = getProductHandleFromPath();
      if (!handle) return null;
      var url = '/apps/zakeke/c/' + handle + '?composition=' + encodeURIComponent(edit.composition);
      if (edit.variant) url += '&variant=' + encodeURIComponent(edit.variant);
      return url;
    }

    var productId = mount ? mount.getAttribute('data-zakeke-product-id') : null;
    if (!productId) return null;

    var pidUrl = ZAKEKE_APP_PROXY_PATH + '?pid=' + encodeURIComponent(productId);
    if (edit.design) pidUrl += '&design=' + encodeURIComponent(edit.design);
    if (edit.quantity) pidUrl += '&quantity=' + encodeURIComponent(edit.quantity);
    return pidUrl;
  }

  /* Zakeke's stylesheet (portal.zakeke.com/.../customizer.css) is built
     for its normal full-page usage and sets a global `body { overflow:
     hidden }` rule of its own -- confirmed live on WRW by disabling the
     cloned <link> and watching document.body's computed overflow flip
     from hidden back to visible. Since we clone this stylesheet into
     <head> once, if it's never disabled that rule stays active (silently
     breaking page scroll) for the rest of the session after the first
     time the modal was opened. Tracked here so it can be disabled
     specifically when the modal closes (Zakeke still needs its own
     styles while the modal is open, so it's re-enabled on reopen -- not
     just deleted outright).

     Worth double-checking on XP specifically: XP's own site-wide modal
     scroll-lock (assets/global.js's siteOverlay component, which still
     adds/removes a "scroll-fixed" class on <body>) currently has no
     matching CSS rule -- it's commented out in assets/global-header.css.
     So background-page scroll isn't blocked behind any of XP's existing
     modals today; this Zakeke-stylesheet toggle will be the only thing
     that ever touches body scroll-locking on this template. */
  var zakekeStylesheetLinks = [];
  function setZakekeStylesheetEnabled(enabled) {
    zakekeStylesheetLinks.forEach(function (link) {
      link.disabled = !enabled;
    });
  }

  /* Preconnect hints for the three cross-origin hosts the browser talks to
     directly during a Zakeke modal open -- the same three hosts confirmed
     via live Network-panel profiling on WristCo (Zakeke's multi-tenant
     SaaS backend, not per-store infrastructure, so the same hosts apply
     here). /apps/zakeke itself is same-origin (a Shopify App Proxy path),
     so it needs no preconnect. Fired on the same hover/touchstart trigger
     as the prefetch below so the connection is already warm by the time
     Zakeke's own script needs it. Not independently re-profiled against a
     live XP page (no store URL available) -- if XP's behavior ever
     diverges from WristCo's, re-check with the Network panel. */
  var ZAKEKE_PRECONNECT_HOSTS = [
    'https://portal.zakeke.com',
    'https://api.zakeke.com',
    'https://customizer-worker.zakeke.com'
  ];
  var preconnectsAdded = false;
  function ensureZakekePreconnects() {
    if (preconnectsAdded) return;
    preconnectsAdded = true;
    ZAKEKE_PRECONNECT_HOSTS.forEach(function (href) {
      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      document.head.appendChild(link);
    });
  }

  /* sessionStorage cache for the raw /apps/zakeke response text, keyed by
     the exact fetch URL (pid plus any design/composition/variant/quantity
     params). zakekeLoadState only guards a single page view -- this
     covers reopening the same product+design later in the same tab
     session (a different page view, so that guard has already reset).
     Wrapped in try/catch: sessionStorage can throw in private browsing or
     once quota is exceeded, and failing to cache should never block the
     actual load. */
  var ZAKEKE_SESSION_CACHE_PREFIX = 'zakeke_c2_fragment::';
  function readZakekeSessionCache(fetchUrl) {
    try {
      return sessionStorage.getItem(ZAKEKE_SESSION_CACHE_PREFIX + fetchUrl);
    } catch (err) {
      return null;
    }
  }
  function writeZakekeSessionCache(fetchUrl, html) {
    try {
      sessionStorage.setItem(ZAKEKE_SESSION_CACHE_PREFIX + fetchUrl, html);
    } catch (err) {
      /* Quota exceeded or storage unavailable -- non-fatal. */
    }
  }

  /* Fetches and parses the Zakeke fragment ahead of the real click --
     fired on hover (desktop mouseenter) / touchstart (mobile), the
     instant a visitor shows intent, instead of waiting for the click that
     actually opens the modal. Only the fetch + DOMParser work happens
     here; nothing is appended to the live DOM and no <script> runs yet
     (that stays gated behind the real click, in openZakekeCustomizer's
     .then() below) -- prefetching a design a visitor never actually opens
     shouldn't clone stylesheets or boot Zakeke's iframe for nothing.

     Memoized on zakekePrefetchPromise (module-scoped: there is exactly
     one Zakeke container per page): a hover followed by a click, the
     normal case, reuses the same in-flight/resolved promise instead of
     fetching twice, and a click with no prior hover (keyboard activation,
     or a tap where touchstart and click land close together) still works
     by starting the fetch right there in openZakekeCustomizer's own call
     to this function. */
  var zakekePrefetchPromise = null;
  function prefetchZakeke(mount) {
    if (zakekePrefetchPromise) return zakekePrefetchPromise;

    ensureZakekePreconnects();

    var fetchUrl = buildZakekeFetchUrl(mount);
    if (!fetchUrl) {
      zakekePrefetchPromise = Promise.reject(new Error('No Zakeke fetch URL could be built'));
      return zakekePrefetchPromise;
    }

    var cached = readZakekeSessionCache(fetchUrl);
    var htmlPromise = cached !== null
      ? Promise.resolve(cached)
      : fetch(fetchUrl)
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Zakeke request failed with status ' + response.status);
            }
            return response.text();
          })
          .then(function (html) {
            writeZakekeSessionCache(fetchUrl, html);
            return html;
          });

    zakekePrefetchPromise = htmlPromise.then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var scope =
        doc.querySelector('.icartShopifyCartContent') ||
        doc.querySelector('#MainContent') ||
        doc.body;

      if (!scope.querySelector('#zakeke-container')) {
        /* Most likely cause: /apps/zakeke redirected (e.g. the NIVA gate
           theme.liquid applies) instead of returning the customizer. */
        throw new Error('Zakeke container missing from /apps/zakeke response');
      }

      return { scope: scope };
    });

    return zakekePrefetchPromise;
  }

  function openZakekeCustomizer() {
    var mount = document.querySelector('[data-zakeke-mount]');
    if (!mount || zakekeLoadState) return;

    if (!buildZakekeFetchUrl(mount)) {
      zakekeLoadState = 'error';
      mount.innerHTML =
        '<p class="xp-c2-zakeke-error">This product is not set up for customization yet.</p>';
      return;
    }

    zakekeLoadState = 'loading';

    prefetchZakeke(mount)
      .then(function (prepared) {
        var scope = prepared.scope;

        var loading = mount.querySelector('[data-zakeke-loading]');
        if (loading) loading.remove();

        /* Fresh element, not a reused/cleared static one -- matches
           WristCo's own appendZakekeFragment() (assets/custom-js-
           customizer-2.js) exactly, per direct comparison: #zakeke-
           container doesn't exist in this file's markup at all until this
           point. The explicit inline min-height, on top of the CSS
           min-height rules for .xp-c2-zakeke-mount #zakeke-container, is
           set on creation so Zakeke's SDK always has real dimensions to
           measure the instant its script boots, instead of depending on
           the CSS flex/100dvh chain having already finished resolving
           layout by that exact moment. */
        var container = document.createElement('div');
        container.id = 'zakeke-container';
        container.style.minHeight = '60vh';
        mount.appendChild(container);

        var sourceForm = scope.querySelector('#zakeke-addtocart');
        if (sourceForm && !document.getElementById('zakeke-addtocart')) {
          mount.appendChild(sourceForm.cloneNode(true));
        }

        scope.querySelectorAll('link[href*="zakeke"]').forEach(function (link) {
          var href = link.getAttribute('href');
          if (href && !document.querySelector('link[href="' + href + '"]')) {
            var cloned = link.cloneNode(true);
            document.head.appendChild(cloned);
            zakekeStylesheetLinks.push(cloned);
          }
        });

        /* Appended into the mount, alongside #zakeke-container, matching
           WristCo's appendZakekeFragment() exactly -- previously appended
           to document.body, detached from the mount/container entirely. */
        scope.querySelectorAll('script').forEach(function (oldScript) {
          var newScript = document.createElement('script');
          for (var i = 0; i < oldScript.attributes.length; i++) {
            var attr = oldScript.attributes[i];
            newScript.setAttribute(attr.name, attr.value);
          }
          newScript.textContent = oldScript.textContent;
          mount.appendChild(newScript);
        });

        zakekeLoadState = 'loaded';
      })
      .catch(function (err) {
        console.error('Zakeke customizer could not be initialized:', err);
        zakekeLoadState = 'error';
        mount.innerHTML =
          '<p class="xp-c2-zakeke-error">Sorry, the customizer could not be loaded. Please try again.</p>';
      });
  }

  function initZakekePopup() {
    var modalEl = document.getElementById('ZakekeCustomizer2Modal');
    if (!modalEl) return;

    /* querySelectorAll, not querySelector: the sticky mobile "Start
       Customizing" bar duplicates this trigger, so there can be more than
       one on the page. openZakekeCustomizer() is itself idempotent
       (guarded by zakekeLoadState), so wiring every match is safe even if
       more than one ends up firing it. */
    document.querySelectorAll('[data-zakeke-trigger]').forEach(function (trigger) {
      /* Hover (desktop) / touchstart (mobile): fire the fetch + parse the
         instant a visitor shows intent, well before the click that
         actually opens the modal. { once: true } per-listener is safe
         even with two triggers sharing one container, since
         prefetchZakeke() is itself memoized globally. */
      trigger.addEventListener('mouseenter', function () {
        var mount = document.querySelector('[data-zakeke-mount]');
        if (mount) prefetchZakeke(mount);
      }, { once: true });
      trigger.addEventListener('touchstart', function () {
        var mount = document.querySelector('[data-zakeke-mount]');
        if (mount) prefetchZakeke(mount);
      }, { once: true, passive: true });

      trigger.addEventListener('click', openZakekeCustomizer);
    });
  }

  /* ---------------------------------------------------------------------
   * Hide the on-site chat widget while either pop-up is open
   * ------------------------------------------------------------------- */
  /* Toggles a body class rather than hiding matched elements directly, so
     it applies continuously to any current or future matching element --
     no timing dependency on exactly when the chat app's script injects
     its widget relative to a modal opening (confirmed as a real failure
     mode on WRW: the widget's script loaded *after* the modal was already
     open, which a one-time direct-hide-at-open-time missed entirely).
     See section-product-customizer-2-zakeke-popup.liquid for the CSS rule
     this toggles, and why !important is required there, and for which
     chat widget this targets (NEEDS LIVE CONFIRMATION ON XP -- see that
     file's comment). */
  function setChatWidgetHidden(hidden) {
    document.body.classList.toggle('xp-c2-hide-chat-widget', hidden);
  }

  /* Tracked separately per modal (not one shared boolean) so that closing
     one modal can't prematurely reveal the widget while the other is
     still open. */
  var chatHiddenBy = { zakeke: false, color: false };
  function recomputeChatVisibility() {
    setChatWidgetHidden(chatHiddenBy.zakeke || chatHiddenBy.color);
  }

  function watchModalOpenState(modalInner, onChange) {
    if (!modalInner) return;
    function sync() {
      onChange(modalInner.classList.contains('open'));
    }
    sync();
    if (window.MutationObserver) {
      var observer = new MutationObserver(sync);
      observer.observe(modalInner, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* Also triggers the color-modal image preload above -- both need the
     same "did this modal just open" signal, so they share one observer
     per modal instead of registering two. */
  function initModalOpenWatchers() {
    var zakekeModalEl = document.getElementById('ZakekeCustomizer2Modal');
    var zakekeInner = zakekeModalEl ? zakekeModalEl.querySelector('.modal') : null;
    watchModalOpenState(zakekeInner, function (isOpen) {
      /* Belt-and-suspenders alongside the click listeners in
         initZakekePopup(): matches WristCo's own dual-trigger pattern.
         openZakekeCustomizer() is idempotent, so this is a safety net,
         not a second load. */
      if (isOpen) {
        openZakekeCustomizer();
      }
      setZakekeStylesheetEnabled(isOpen);
      chatHiddenBy.zakeke = isOpen;
      recomputeChatVisibility();
    });

    var colorModalEl = document.getElementById('ColorPopup2Modal');
    var colorInner = colorModalEl ? colorModalEl.querySelector('.modal') : null;
    watchModalOpenState(colorInner, function (isOpen) {
      if (isOpen && colorModalEl) {
        preloadColorPreviewImages(colorModalEl);
      }
      chatHiddenBy.color = isOpen;
      recomputeChatVisibility();
    });
  }

  /* ---------------------------------------------------------------------
   * Sticky mobile "Start Customizing" bar
   * ------------------------------------------------------------------- */
  /* Mirrors this theme's own existing sticky-add-to-cart pattern
     (assets/template-product.js's stickyCheckout(): an IntersectionObserver
     on the real on-page button toggling a class on a fixed-position
     duplicate, animated via transform: translateY rather than display) and
     WristCo's co-customizer2-hero.liquid: shows the duplicate fixed-position
     button once the real on-page button scrolls out of view, hides it again
     once scrolled back to it. */
  function initStickyStartButton() {
    var onPageBtn = document.querySelector('[data-c2-start-onpage]');
    var stickyBar = document.querySelector('[data-c2-sticky-start]');
    if (!onPageBtn || !stickyBar || !window.IntersectionObserver) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyBar.classList.remove('active');
        } else {
          stickyBar.classList.add('active');
        }
      });
    });
    observer.observe(onPageBtn);
  }

  document.addEventListener('DOMContentLoaded', function () {
    zakekeEditParams = readZakekeEditParamsFromUrl();

    initColorPopup();
    initZakekePopup();
    initModalOpenWatchers();
    initStickyStartButton();

    /* Edit Design's whole point is landing already open, pre-loaded with
       the existing design -- same modal.show() the click handlers wired
       in initZakekePopup() already use, just invoked on page load
       instead of a click. initModalOpenWatchers()'s MutationObserver
       (registered just above) is what actually calls
       openZakekeCustomizer() once the "open" class lands, same as a
       real click would trigger. */
    if (zakekeEditParams.hasEditParam) {
      var modalEl = document.getElementById('ZakekeCustomizer2Modal');
      if (modalEl && typeof modalEl.show === 'function') {
        modalEl.show();
      }
    }
  });
})();
